/**
 * Factory for localStorage-backed wizard draft stores, namespaced per tenant.
 *
 * The scheme wizard and the rule wizard each shipped their own byte-identical
 * copy of this; membership would have been the third, so it lives here now.
 * Those two are left alone deliberately — they work, and rewriting them is a
 * separate change.
 *
 * Draft shape: { id, name, currentStep, updatedAt, data }
 * `data` must stay JSON-safe: store uploaded files as URL strings, never File
 * objects.
 */

const tenantId = () => {
  try {
    return JSON.parse(sessionStorage.getItem("vendorData"))?.tenant_id ?? "unknown";
  } catch {
    return "unknown";
  }
};

export function makeDraftStore(prefix) {
  const storageKey = () => `${prefix}_${tenantId()}`;

  function listDrafts() {
    try {
      const raw = localStorage.getItem(storageKey());
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr)
        ? arr.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
        : [];
    } catch {
      return [];
    }
  }

  function getDraft(id) {
    return listDrafts().find((d) => String(d.id) === String(id)) || null;
  }

  /** Upsert a draft by id. Returns the saved draft (with fresh updatedAt). */
  function saveDraft(draft) {
    const drafts = listDrafts();
    const saved = { ...draft, updatedAt: Date.now() };
    const idx = drafts.findIndex((d) => String(d.id) === String(saved.id));
    if (idx >= 0) drafts[idx] = saved;
    else drafts.push(saved);
    try {
      localStorage.setItem(storageKey(), JSON.stringify(drafts));
    } catch (e) {
      console.error(`${prefix}.saveDraft failed`, e);
    }
    return saved;
  }

  function deleteDraft(id) {
    const drafts = listDrafts().filter((d) => String(d.id) !== String(id));
    try {
      localStorage.setItem(storageKey(), JSON.stringify(drafts));
    } catch (e) {
      console.error(`${prefix}.deleteDraft failed`, e);
    }
  }

  return { listDrafts, getDraft, saveDraft, deleteDraft };
}
